/** Jornada "actual" de una eliminatoria (pedido Eduardo 2026-08-15).
 *
 * Bug del torneo 58: al abrir Calendario el filtro caía en "3er lugar"
 * porque se buscaba la ÚLTIMA ronda con partidos pendientes — con todo sin
 * jugar, eso es el final del bracket. La jornada actual es la PRIMERA (en
 * orden de bracket) con pendientes: el torneo avanza de adelante hacia
 * atrás. Con todo jugado, cae en la última — el torneo terminó y se muestra
 * el cierre.
 */
export function jornadaActualEliminatoria(
  orderedLabels: string[],
  labelsConPendientes: ReadonlySet<string>,
): string | null {
  const primeraAbierta = orderedLabels.find((label) => labelsConPendientes.has(label));
  return primeraAbierta ?? orderedLabels[orderedLabels.length - 1] ?? null;
}

/** Fechas de una jornada (AAAA-MM-DD, día de calendario sin zona horaria). */
export interface VentanaJornada {
  numero: number;
  inicio: string;
  fin: string;
}

function dia(iso: string | null | undefined): string | null {
  const found = /^\d{4}-\d{2}-\d{2}/.exec((iso ?? "").trim());
  return found ? found[0] : null;
}

/** Ventana de cada jornada según sus partidos con fecha: del primer al último
 * día programado (sin contar cancelados; eso lo filtra quien llama). */
export function ventanasDePartidos(
  partidos: Array<{ numero: number | null; fecha: string | null | undefined }>,
): VentanaJornada[] {
  const porNumero = new Map<number, VentanaJornada>();
  for (const partido of partidos) {
    const fecha = dia(partido.fecha);
    if (partido.numero === null || fecha === null) continue;
    const actual = porNumero.get(partido.numero);
    if (!actual) {
      porNumero.set(partido.numero, { numero: partido.numero, inicio: fecha, fin: fecha });
      continue;
    }
    if (fecha < actual.inicio) actual.inicio = fecha;
    if (fecha > actual.fin) actual.fin = fecha;
  }
  return [...porNumero.values()];
}

/** Junta varias ventanas de la MISMA jornada (p. ej. la Jornada 2 de cada
 * categoría con «Todas las categorías»): del primer inicio al último fin. */
export function unirVentanas(ventanas: VentanaJornada[]): VentanaJornada[] {
  const porNumero = new Map<number, VentanaJornada>();
  for (const ventana of ventanas) {
    const inicio = dia(ventana.inicio);
    const fin = dia(ventana.fin);
    if (inicio === null || fin === null || fin < inicio) continue;
    const actual = porNumero.get(ventana.numero);
    if (!actual) {
      porNumero.set(ventana.numero, { numero: ventana.numero, inicio, fin });
      continue;
    }
    if (inicio < actual.inicio) actual.inicio = inicio;
    if (fin > actual.fin) actual.fin = fin;
  }
  return [...porNumero.values()];
}

function sumarDias(isoDia: string, dias: number): string {
  const [anio, mes, diaDelMes] = isoDia.split("-").map(Number);
  return new Date(Date.UTC(anio, mes - 1, diaDelMes + dias)).toISOString().slice(0, 10);
}

/** Sugerencia del formato para cada jornada: Inicio + «Días entre Jornadas»
 * (7 si no hay dato). Misma cuenta que el servidor
 * (`rounds.jornada_dates._suggested_window` y `rounds.services._round_window`):
 * la jornada N va de Inicio + días × (N − 1) a ese día + días − 1. */
export function ventanasSugeridas({
  jornadas,
  inicio,
  diasEntreJornadas,
}: {
  jornadas: number[];
  inicio: string | null | undefined;
  diasEntreJornadas: unknown;
}): VentanaJornada[] {
  const inicioDia = dia(inicio);
  if (inicioDia === null) return [];
  const parsed = Math.trunc(Number(diasEntreJornadas));
  const dias = diasEntreJornadas && Number.isFinite(parsed) ? Math.max(1, parsed) : 7;
  return [...new Set(jornadas)].map((numero) => {
    const desde = sumarDias(inicioDia, dias * (numero - 1));
    return { numero, inicio: desde, fin: sumarDias(desde, dias - 1) };
  });
}

/** Fechas de cada jornada por prioridad (igual que el servidor,
 * `_jornada_dates_window`): de cada número cuenta la PRIMERA fuente que lo
 * trae —las guardadas; si no, las de sus partidos programados; si tampoco, la
 * sugerencia del formato—. Una fuente puede traer varias ventanas del mismo
 * número (una por categoría): se conservan todas y luego se unen. */
export function ventanasPorPrioridad(fuentes: VentanaJornada[][]): VentanaJornada[] {
  const cubiertas = new Set<number>();
  const resultado: VentanaJornada[] = [];
  for (const fuente of fuentes) {
    const deEsta = fuente.filter((ventana) => !cubiertas.has(ventana.numero));
    resultado.push(...deEsta);
    for (const ventana of deEsta) cubiertas.add(ventana.numero);
  }
  return resultado;
}

/** Resultados sin jugar: W.O., no presentado y default. */
const RESULTADOS_SIN_JUGAR = new Set(["walkover", "no_show", "defaulted", "double_walkover"]);

/** Eduardo 2026-10-05: dar de baja a un jugador cierra con W.O. TODOS sus
 * pendientes, también los de jornadas futuras; esos W.O. (y los «no
 * presentado» o default) contra alguien dado de baja no cuentan como «la
 * jornada más alta con resultado», o una baja adelantaría la jornada actual
 * de toda la categoría. Misma regla que el servidor
 * (`rounds.jornada_dates.current_jornada_number`). Sin el estado del
 * participante en el partido (payloads viejos) cuenta como resultado. */
export function esResultadoDeBaja(partido: {
  status: string | null | undefined;
  player1?: { current_status?: string | null } | null;
  player2?: { current_status?: string | null } | null;
}): boolean {
  if (!RESULTADOS_SIN_JUGAR.has(String(partido.status ?? ""))) return false;
  return partido.player1?.current_status === "dropped" || partido.player2?.current_status === "dropped";
}

/** Jornada actual de una categoría (definición compartida, Eduardo
 * 2026-10-05: «sólo deben ser los de la jornada actual (1), no los futuros»).
 * Es la misma regla que usa el servidor para el «Pendiente» del Resumen
 * (`rounds.jornada_dates.current_jornada_number`), para que Calendario,
 * Resultados y el Resumen digan la misma jornada el mismo día.
 *
 * a) Con fechas (ver `ventanasPorPrioridad`): la jornada cuyo rango incluye
 *    `hoy`; si hoy cae entre jornadas, la última que ya empezó. En los dos
 *    casos es la más alta que ya empezó (así una jornada con un partido
 *    pospuesto semanas después no detiene a las siguientes).
 * b) Nunca antes de la jornada más alta que ya tenga algún resultado (sin
 *    contar los W.O. de una baja: quien llama los quita con
 *    `esResultadoDeBaja`).
 * c) Si nada de eso aplica: la primera jornada.
 *
 * Solo devuelve números de `jornadas` (las que existen en pantalla); null si
 * no hay ninguna. */
export function jornadaActualNumero({
  jornadas,
  ventanas,
  conResultado,
  hoy,
}: {
  jornadas: number[];
  ventanas: VentanaJornada[];
  conResultado: number[];
  hoy: string;
}): number | null {
  const existentes = [...new Set(jornadas)].sort((a, b) => a - b);
  if (existentes.length === 0) return null;
  const existe = new Set(existentes);
  const hoyDia = dia(hoy);

  let actual: number | null = null;
  if (hoyDia !== null) {
    const validas = unirVentanas(ventanas.filter((ventana) => existe.has(ventana.numero)));
    const yaEmpezaron = validas.filter((ventana) => ventana.inicio <= hoyDia).map((ventana) => ventana.numero);
    actual = yaEmpezaron.length > 0 ? Math.max(...yaEmpezaron) : null;
  }
  if (actual === null) actual = existentes[0];

  const conResultadoExistentes = conResultado.filter((numero) => existe.has(numero));
  if (conResultadoExistentes.length > 0) {
    actual = Math.max(actual, ...conResultadoExistentes);
  }
  return actual;
}
